/**
 * Design System - Token Utilities
 *
 * Helper functions to use design system tokens in components
 */

import { colors } from '../tokens/colors';
import { spacing } from '../tokens/spacing';
import { typography } from '../tokens/typography';
import { breakpoints } from '../tokens/breakpoints';
import { borders } from '../tokens/borders';
import { shadows } from '../tokens/shadows';
import { dimensions } from '../tokens/dimensions';

// Note: getColor and getSpacing are exported from their respective token files
// to avoid conflicts with the versions here

/**
 * Get typography value from the design system
 * @example getTypography('size', '5xl') // '48px'
 */
export const getTypography = (
  category: keyof typeof typography,
  variant: string
) => {
  const typographyCategory = typography[category] as Record<
    string,
    string | number
  >;
  return typographyCategory[variant];
};

/**
 * Get breakpoint value from the design system
 * @example getBreakpoint('md') // '768px'
 */
export const getBreakpoint = (breakpoint: keyof typeof breakpoints) => {
  return breakpoints[breakpoint];
};

/**
 * Get border radius from the design system
 * @example getBorderRadius('md') // '8px'
 */
export const getBorderRadius = (size: keyof typeof borders.radius) => {
  return borders.radius[size];
};

/**
 * Get shadow from the design system
 * @example getShadow('inner', 'sm') // 'inset 0px 1px 2px 0px rgba(0, 0, 0, 0.05)'
 */
export const getShadow = (category: keyof typeof shadows, variant?: string) => {
  if (variant && typeof shadows[category] === 'object') {
    const shadowCategory = shadows[category] as Record<string, string>;
    return shadowCategory[variant];
  }
  return shadows[category] as string;
};

// Export all tokens for direct access
export {
  colors,
  spacing,
  typography,
  breakpoints,
  borders,
  shadows,
  dimensions,
};

// Export convenience tokens
export {
  brandColors,
  backgroundColors,
  typographyColors,
  strokeColors,
  statusColors,
} from '../tokens/colors';

export { fontPrimary, fontSecondary } from '../tokens/typography';

export { componentSpacing, layoutSpacing } from '../tokens/spacing';

export { componentBorders } from '../tokens/borders';

export { componentShadows } from '../tokens/shadows';

export { mediaQueries } from '../tokens/breakpoints';

export { componentAnimations } from '../tokens/animations';

// Tailwind CSS classes using design system tokens
export const dsClasses = {
  // Colors
  colors: {
    brand: {
      primary: 'bg-ds-brand-primary text-ds-text-primary',
      secondary: 'bg-ds-brand-secondary text-ds-text-primary',
    },
    text: {
      primary: 'text-ds-text-primary',
      secondary: 'text-ds-text-secondary',
      tertiary: 'text-ds-text-tertiary',
    },
    bg: {
      primary: 'bg-ds-bg-primary',
      secondary: 'bg-ds-bg-secondary',
    },
  },

  // Typography
  typography: {
    h1: 'text-5xl font-semibold leading-[52px] tracking-[-0.5px]',
    h2: 'text-4xl font-semibold leading-normal',
    h3: 'text-2xl font-bold leading-normal',
    h4: 'text-xl font-semibold',
    body: 'text-base font-normal',
    caption: 'text-sm font-normal',
    small: 'text-xs font-normal',
  },

  // Spacing
  spacing: {
    section: 'py-20 px-20',
    container: 'max-w-[1400px] mx-auto',
    card: 'p-6',
  },
};
